/**
 * Notification system for displaying toast messages
 * @typedef {Object} NotificationObject
 * @property {string} id
 * @property {'success' | 'error' | 'warn' | 'info'} type
 * @property {string} title
 * @property {string} [text]
 * @property {number} duration
 */

let notificationIdCounter = 0;
const notifications = new Map();

/**
 * Creates and shows a notification
 * @param {Object} options - Notification options
 * @param {'success' | 'error' | 'warn' | 'info'} options.type - Notification type
 * @param {string} options.title - Notification title
 * @param {string} [options.text] - Optional notification text
 * @param {number} [options.duration] - Duration in milliseconds (default: 5000)
 * @returns {string} Notification ID
 */
export function notify(options) {
  const id = `notification-${++notificationIdCounter}`;
  const duration = options.duration ?? 5000;

  const notification = {
    id,
    type: options.type,
    title: options.title,
    text: options.text,
    duration,
  };

  notifications.set(id, notification);
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => renderNotification(notification));
  } else {
    renderNotification(notification);
  }

  if (duration > 0) {
    setTimeout(() => {
      removeNotification(id);
    }, duration);
  }

  return id;
}

/**
 * Removes a notification by ID
 * @param {string} id - Notification ID
 */
export function removeNotification(id) {
  const notification = notifications.get(id);
  if (!notification) return;

  const element = document.getElementById(id);
  if (element) {
    element.classList.add('notification-leaving');
    setTimeout(() => {
      element.remove();
    }, 200);
  }

  notifications.delete(id);
}

/**
 * Renders a notification element
 * @param {NotificationObject} notification - Notification object
 */
function renderNotification(notification) {
  const container = getOrCreateContainer();
  if (!container) {
    console.error('Failed to create notification container');
    return;
  }

  const element = document.createElement('div');
  element.id = notification.id;
  element.setAttribute('role', 'alert');
  element.setAttribute('aria-live', notification.type === 'error' ? 'assertive' : 'polite');

  let bgColor = '#4a4a4a';
  if (notification.type === 'error') {
    bgColor = '#6d2318';
  } else if (notification.type === 'warn') {
    bgColor = '#8B4513';
  } else if (notification.type === 'info') {
    bgColor = '#1e3a5f';
  } else if (notification.type === 'success') {
    bgColor = '#4a4a4a';
  }

  element.className = `notification notification-${notification.type} notification-entering pointer-events-auto cursor-pointer`;
  element.style.cssText = `background-color: ${bgColor}; color: white; padding: 0.25rem 1rem; text-align: center; transition: all 0.2s ease-in-out; pointer-events: auto; cursor: pointer;`;
  
  element.innerHTML = `
    <div style="font-weight: normal; font-size: 1rem; line-height: 1.5rem;">${escapeHtml(notification.title)}</div>
    ${notification.text ? `<div style="font-size: 0.875rem; margin-top: 0.125rem;">${escapeHtml(notification.text)}</div>` : ''}
  `;

  element.addEventListener('click', () => removeNotification(notification.id));

  container.appendChild(element);

  requestAnimationFrame(() => {
    element.classList.remove('notification-entering');
  });
}

/**
 * Gets or creates the notification container
 * @returns {HTMLElement | null} Container element
 */
function getOrCreateContainer() {
  let container = document.getElementById('notifications-container');
  if (!container) {
    if (!document.body) {
      console.error('document.body is not available');
      return null;
    }
    container = document.createElement('div');
    container.id = 'notifications-container';
    container.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; z-index: 9999; pointer-events: none; display: flex; flex-direction: column; gap: 1px;';
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Escapes HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convenience methods
 * @type {{ success: (title: string, text?: string, duration?: number) => string, error: (title: string, text?: string, duration?: number) => string, warn: (title: string, text?: string, duration?: number) => string, info: (title: string, text?: string, duration?: number) => string }}
 */
export const notification = {
  success: (title, text, duration) => notify({ type: 'success', title, text, duration }),
  error: (title, text, duration) => notify({ type: 'error', title, text, duration }),
  warn: (title, text, duration) => notify({ type: 'warn', title, text, duration }),
  info: (title, text, duration) => notify({ type: 'info', title, text, duration }),
};

/**
 * Shows a countdown notification with click-to-cancel behavior.
 * @param {Object} options
 * @param {string} options.titleTemplate
 * @param {string} [options.text]
 * @param {number} [options.countdownSeconds]
 * @param {'success' | 'error' | 'warn' | 'info'} [options.type]
 * @returns {Promise<'completed' | 'cancelled'>}
 */
export function notifyWithCountdown(options) {
  const countdownSeconds = options.countdownSeconds ?? 5;
  const type = options.type ?? 'info';

  return new Promise((resolve) => {
    let secondsLeft = countdownSeconds;
    /** @type {ReturnType<typeof setInterval> | null} */
    let intervalId = null;
    let notificationId = null;
    let cancelled = false;

    const updateTitle = () => options.titleTemplate.replace('{seconds}', String(secondsLeft));

    notificationId = notify({
      type,
      title: updateTitle(),
      text: options.text,
      duration: 0,
    });

    const element = document.getElementById(notificationId);
    if (element) {
      element.onclick = (event) => {
        event.stopPropagation();
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
        removeNotification(notificationId);
        resolve('cancelled');
      };
    }

    intervalId = setInterval(() => {
      secondsLeft--;

      if (secondsLeft <= 0) {
        if (intervalId) {
          clearInterval(intervalId);
        }
        removeNotification(notificationId);
        if (!cancelled) resolve('completed');
        return;
      }

      if (element && !cancelled) {
        const titleElement = element.querySelector('div');
        if (titleElement) {
          titleElement.textContent = updateTitle();
        }
      }
    }, 1000);
  });
}

if (typeof window !== 'undefined') {
  /** @type {any} */
  (window).notification = notification;
}
